import { datadogLogs } from "@datadog/browser-logs";
import { finalSaleReturnsCoverageCalculator } from "@redotech/redo-model/coverage-calculator/final-sale-returns";
import { CustomerInfo } from "@redotech/redo-model/coverage-calculator/index";
import { packageProtectionCoverageCalculator } from "@redotech/redo-model/coverage-calculator/package-protection";
import { returnCoverageCalculator } from "@redotech/redo-model/coverage-calculator/returns";
import { CoverageInfo, WidgetAutocheck } from "@redotech/redo-model/widget";
import {
  ExtensionShopifyClient,
  ShopifyCart,
  ShopifyCartItem,
} from "@redotech/shopify-client/ajax";
import { ShopifyExtensionClient } from "../client/shopify-extension-client";
import { autocheckToCoverageSettings } from "../eligibility/autocheck-conversion";
import { getTagsAndCollections } from "../eligibility/returns-coverage-eligibility";
import { log } from "../monitoring/log";

async function cartItemToItemInfo({
  cartItem,
  autocheck,
  shopifyClient,
  redoClient,
  onTapcart,
}: {
  cartItem: ShopifyCartItem;
  autocheck: WidgetAutocheck;
  shopifyClient: ExtensionShopifyClient;
  redoClient: ShopifyExtensionClient;
  onTapcart: boolean;
}) {
  const { tags, collections } = await getTagsAndCollections({
    cartItem,
    autocheck,
    shopifyClient,
    redoClient,
  });

  const properties: string[] = cartItem.properties
    ? Object.keys(cartItem.properties).reduce((acc, key) => {
        const value = cartItem.properties?.[key];
        acc.push(key);
        if (value !== null && value !== "") {
          acc.push(`${key}: ${value}`);
        }
        return acc;
      }, [] as string[])
    : [];

  let price =
    cartItem.discounted_price ||
    (cartItem.line_price ? cartItem.line_price / cartItem.quantity : 0);
  price = onTapcart ? price : price / 100;

  return {
    price,
    quantity: cartItem.quantity,
    collections: collections.map((c) => c.title),
    productTags: tags,
    properties,
    vendor: cartItem.vendor || "",
    discounts: cartItem.discounts?.map((d) => ({ name: d.title })) || [],
    isFinalSale: false,
    hasSubscription: !!cartItem.selling_plan,
    deliveryProfileId: null,
  };
}

function convertToCents({
  dollars,
  onTapcart,
}: {
  dollars: number | undefined;
  onTapcart: boolean;
}) {
  if (!dollars) return 0;

  // tapcart money is already in dollars
  if (onTapcart) return dollars;

  return Math.floor(dollars * 100);
}

export async function getCoverageInfoFromCoverageCalculators({
  autocheck,
  customerInfo,
  cart,
  shopifyClient,
  redoClient,
  onTapcart,
}: {
  autocheck: WidgetAutocheck;
  customerInfo: CustomerInfo;
  cart: ShopifyCart;
  shopifyClient: ExtensionShopifyClient;
  redoClient: ShopifyExtensionClient;
  onTapcart: boolean;
}) {
  const teamSettings = autocheckToCoverageSettings(autocheck);

  const nonRedoItems = cart.items.filter((item) => item.vendor !== "re:do");
  const items = await Promise.all(
    nonRedoItems.map((item) =>
      cartItemToItemInfo({
        cartItem: item,
        autocheck,
        shopifyClient,
        redoClient,
        onTapcart,
      }),
    ),
  );

  const returnCoverageInfo = returnCoverageCalculator.getCoverage(
    teamSettings,
    customerInfo,
    items,
  );
  const packageProtectionCoverageInfo =
    packageProtectionCoverageCalculator.getCoverage(
      teamSettings,
      customerInfo,
      items,
    );
  const finalSaleReturnsCoverageInfo =
    finalSaleReturnsCoverageCalculator.getCoverage(
      teamSettings,
      customerInfo,
      items,
    );

  const returnPrice = convertToCents({
    dollars: returnCoverageInfo.price,
    onTapcart,
  });
  const packageProtectionPrice = convertToCents({
    dollars: packageProtectionCoverageInfo.price,
    onTapcart,
  });
  const finalSaleReturnsPrice = convertToCents({
    dollars: finalSaleReturnsCoverageInfo.price,
    onTapcart,
  });

  // return coverage is included in final sale coverage. So we need to take the max of the two.
  const returnCoveragePrice = Math.max(returnPrice, finalSaleReturnsPrice);

  const coverageInfo: CoverageInfo = {
    returns: {
      cartEligible:
        returnCoverageInfo.eligible || finalSaleReturnsCoverageInfo.eligible,
      price: returnCoveragePrice,
      isFinalSale: finalSaleReturnsCoverageInfo.eligible,
    },
    packageProtection: {
      cartEligible: packageProtectionCoverageInfo.eligible,
      price: packageProtectionPrice,
    },
    both: {
      cartEligible:
        (returnCoverageInfo.eligible ||
          finalSaleReturnsCoverageInfo.eligible) &&
        packageProtectionCoverageInfo.eligible,
      price: returnCoveragePrice + packageProtectionPrice,
    },
  };

  return coverageInfo;
}

export function compareCoverageInfos({
  oldCoverageInfo,
  newCoverageInfo,
  autocheck,
}: {
  oldCoverageInfo: CoverageInfo;
  newCoverageInfo: CoverageInfo;
  autocheck: WidgetAutocheck;
}) {
  if (JSON.stringify(oldCoverageInfo) === JSON.stringify(newCoverageInfo)) {
    log("Coverage infos are the same");
    return true;
  }

  log("Coverage infos are different", { oldCoverageInfo, newCoverageInfo });

  datadogLogs.logger.warn("Coverage infos are different", {
    team: autocheck.teamId,
    widgetSlug: autocheck.widgetSlug,
    oldCoverageInfo,
    newCoverageInfo,
  });
  return false;
}
